// skill-library.js — the five shipped skills. A skill IS an actionLibrary entry
// (id/label/intent/clips/playback/duration) plus the RPG fields, so the existing
// action executor plays it and the Studio clip picker edits it unchanged.
// Pure Yang Palm has an authored strike; the other clips remain placeholders
// from public/animation.json. Swapping them is data, not code.

// Combat range is scene-sized on purpose: the cast walks the whole temple on its own,
// and a 6 m reach meant a duel was unusable more often than not. 18 m covers the
// authored ground (~16 m across) while still refusing someone who has left it.
export const SKILL_LIBRARY = [
  {
    id: 'thuan-duong-chuong',
    label: { vi: 'Thuần Dương Chưởng', en: 'Pure Yang Palm' },
    intent: 'A single explosive palm strike of pure yang qi.',
    clips: [{ id: 'yang-palm' }],
    playback: 'sequence',
    duration: 3,
    ritual: { steps: 3, title: 'Trận pháp I' },
    kind: 'attack',
    usable: ['combat'],
    target: { who: 'enemy', count: 1, range: 18 },
    cost: { mp: 40 },
    cooldown: { rounds: 1 },
    castMs: 1200,
    effects: [{ type: 'damage', base: 90, perLevel: 20, variance: 0.15, element: 'yang' }],
    levels: { max: 5, usesPerLevel: [0, 3, 6, 10, 15] },
    learn: { realmMin: 'luyen-khi', teacher: false, trustMin: 0 },
    icon: '🔥',
    fx: { vfx: 'yang-burst', sfx: '', victimEmote: 'surprised', victimGesture: '' },
    barks: { vi: ['Tiếp chiêu!', 'Nhận lấy!'], en: ['Take this!', 'Here it comes!'] },
  },
  {
    id: 'ho-the-duong-khi',
    label: { vi: 'Hộ Thể Dương Khí', en: 'Yang Body Ward' },
    intent: 'Wraps the caster in a shell of yang qi that eats the next blows.',
    clips: [{ id: 'thinking' }],
    playback: 'sequence',
    duration: 3,
    ritual: { steps: 3, title: 'Trận pháp I' },
    kind: 'shield',
    usable: ['combat'],
    target: { who: 'self', count: 1, range: 0 },
    cost: { mp: 60 },
    cooldown: { rounds: 3 },
    castMs: 1000,
    effects: [{ type: 'shield', base: 150, perLevel: 40, variance: 0, rounds: 3 }],
    levels: { max: 5, usesPerLevel: [0, 3, 6, 10, 15] },
    learn: { realmMin: 'luyen-khi', teacher: false, trustMin: 0 },
    icon: '🛡️',
    fx: { vfx: 'yang-ward', sfx: '', victimEmote: '', victimGesture: '' },
    barks: { vi: ['Hộ thể!'], en: ['Ward up!'] },
  },
  {
    id: 'hap-linh',
    label: { vi: 'Hấp Linh', en: 'Spirit Siphon' },
    intent: 'Pulls spirit energy out of the target and into the caster.',
    clips: [{ id: 'relax' }],
    playback: 'sequence',
    duration: 3,
    ritual: { steps: 5, title: 'Trận pháp II' },
    kind: 'drain',
    usable: ['combat'],
    target: { who: 'enemy', count: 1, range: 18 },
    cost: { mp: 30 },
    cooldown: { rounds: 2 },
    castMs: 1400,
    effects: [{ type: 'drain', stat: 'mp', base: 60, perLevel: 15, variance: 0.1 }],
    levels: { max: 5, usesPerLevel: [0, 3, 6, 10, 15] },
    learn: { realmMin: 'truc-co', teacher: true, trustMin: 40 },
    icon: '🌀',
    fx: { vfx: 'siphon', sfx: '', victimEmote: 'sad', victimGesture: '' },
    barks: { vi: ['Linh khí này, ta nhận!'], en: ['That qi is mine now.'] },
  },
  {
    id: 'tu-luyen',
    label: { vi: 'Tu luyện', en: 'Cultivate' },
    intent: 'Sit at the cultivation spot and refine qi for a year.',
    clips: [{ id: 'sitting' }],
    playback: 'loop',
    duration: 12,
    ritual: { steps: 5, title: 'Trận pháp II' },
    kind: 'cultivate',
    usable: ['free'],
    target: { who: 'self', count: 1, range: 0 },
    cost: {},
    cooldown: { rounds: 0 },
    castMs: 0,
    effects: [{ type: 'cultivate', tier: 'solo' }],
    levels: { max: 1, usesPerLevel: [0] },
    learn: { realmMin: 'luyen-khi', teacher: false, trustMin: 0 },
    icon: '🧘',
    fx: { vfx: 'spiral', sfx: '', victimEmote: '', victimGesture: '' },
    barks: { vi: ['Nhập định.'], en: ['Settling in.'] },
  },
  {
    id: 'song-tu',
    label: { vi: 'Song tu', en: 'Dual Cultivation' },
    intent: 'Cultivate together at the dual spot; both partners grow.',
    clips: [{ id: 'sitting' }],
    playback: 'loop',
    duration: 12,
    ritual: { steps: 7, title: 'Trận pháp III' },
    kind: 'cultivate',
    usable: ['free'],
    // Scene-sized, like the combat skills, not the intimate 3 m it started at. The
    // partner walks the temple on her own, so a tight reach refused song tu far more
    // often than it framed it. What actually anchors the act is the rule that the
    // player must be standing at the dual-cultivation spot; range is only here to
    // rule out a partner who has left the area entirely.
    target: { who: 'ally', count: 1, range: 18 },
    // mpPct is the "25% of max" of rule 6 expressed as data, so a story can retune it.
    cost: { mpPct: 0.25 },
    cooldown: { rounds: 0 },
    castMs: 0,
    effects: [{ type: 'cultivate', tier: 'dual' }],
    levels: { max: 1, usesPerLevel: [0] },
    learn: { realmMin: 'truc-co', teacher: false, trustMin: 0 },
    icon: '☯️',
    fx: { vfx: 'bond', sfx: '', victimEmote: '', victimGesture: '' },
    barks: { vi: ['Cùng ta tu luyện chứ?'], en: ['Cultivate with me?'] },
  },
]

export const SKILL_KINDS = ['attack', 'shield', 'heal', 'buff', 'drain', 'cultivate', 'teach']
export const EFFECT_TYPES = ['damage', 'shield', 'heal', 'drain', 'cultivate', 'storyScore', 'teach']
export const USABLE_MODES = ['combat', 'free']
export const TARGET_WHO = ['self', 'ally', 'enemy', 'any']

export function librarySkill(id) {
  const found = SKILL_LIBRARY.find(s => s.id === id)
  return found ? structuredClone(found) : null
}
export function listLibrarySkills() { return SKILL_LIBRARY.map(s => ({ id: s.id, label: s.label, kind: s.kind, icon: s.icon })) }
